import '@/styles/spin-layout.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IngameFlash, useIngameFlash } from '@/components/IngameFlash';
import { SimpleMenu } from '@/components/MenuGrid';
import { useLocale } from '@/context/LocaleContext';
import { itemIconFallbackUrl, resolveMediaUrl } from '@/lib/assetUrl';
import {
  formatSpinTimeAgo,
  removeSpinWinningCheck,
  runSpinCasinoAnimation,
} from '@/lib/spinCasinoAnimation';
import {
  useSpinMutations,
  useSpinState,
  fetchRecentSpinRewards,
  type SpinRecentRewardFormatted,
  type SpinRecentRewardRow,
  type SpinWheelItem,
} from '@/hooks/useSpin';
import { useSilkBalance } from '@/hooks/useSilkBalance';
import { SilkConfirmModal, type SilkConfirmPhase } from '@/components/SilkConfirmModal';

declare global {
  interface Window {
    bootstrap?: {
      Tooltip: {
        new (
          el: Element,
          opts?: { html?: boolean; placement?: string; customClass?: string },
        ): { dispose: () => void };
        getInstance: (el: Element) => { dispose: () => void } | null;
      };
    };
  }
}

const RESET_SPIN_PRICE = 100;

function initSpinTooltips(container: HTMLElement | null) {
  if (!container || !window.bootstrap?.Tooltip) return;

  container.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((el) => {
    const existing = window.bootstrap!.Tooltip.getInstance(el);
    if (existing) existing.dispose();

    if (el.id === 'spinPlayButton' && !(el as HTMLButtonElement).disabled) return;

    const customClass = el.getAttribute('data-bs-custom-class') ?? undefined;
    new window.bootstrap!.Tooltip(el, {
      html: true,
      placement: 'top',
      ...(customClass ? { customClass } : {}),
    });
  });
}

function RecentRewardItem({
  name,
  image,
  quantity,
  timeLabel,
}: {
  name: string;
  image: string;
  quantity: number;
  timeLabel: string;
}) {
  return (
    <div className="reward-item">
      <div className="reward-image">
        <img
          src={resolveMediaUrl(image)}
          alt={name}
          loading="lazy"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = itemIconFallbackUrl();
          }}
        />
      </div>
      <div className="reward-details">
        <span className="reward-name">{name}</span>
        <span className="reward-amount">x{quantity}</span>
      </div>
      <div className="reward-date">{timeLabel}</div>
    </div>
  );
}

export function SpinPage() {
  const { t } = useLocale();
  const { data, isLoading, isError, refetch } = useSpinState();
  const { spin, deliverReward, resetSpins, fetchRecentSpinRewards } = useSpinMutations();

  const gridRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const cancelAnimRef = useRef<(() => void) | null>(null);
  const winningElRef = useRef<HTMLElement | null>(null);

  const { flash, showFlash } = useIngameFlash(3000);
  const [isSpinning, setIsSpinning] = useState(false);
  const [rewardDelivered, setRewardDelivered] = useState(false);
  const [rewardModalOpen, setRewardModalOpen] = useState(false);
  const [resetDialog, setResetDialog] = useState<{ phase: SilkConfirmPhase; message?: string } | null>(null);
  const resetModalOpen = resetDialog?.phase === 'confirm';
  const { data: silkBalance, isFetching: silkLoading, isError: silkError } = useSilkBalance(resetModalOpen);
  const [wonItem, setWonItem] = useState<SpinWheelItem | null>(null);
  const [recentList, setRecentList] = useState<SpinRecentRewardFormatted[]>([]);
  const [userSpins, setUserSpins] = useState(data?.userSpins ?? null);

  useEffect(() => {
    if (data?.userSpins) setUserSpins(data.userSpins);
  }, [data?.userSpins]);

  useEffect(() => {
    if (!data?.recentRewards) return;
    setRecentList(
      data.recentRewards.map((r: SpinRecentRewardRow) => ({
        name: r.reward_name,
        image: r.reward_image,
        quantity: r.reward_quantity,
        time_ago: formatSpinTimeAgo(r.rewarded_at),
      })),
    );
  }, [data?.recentRewards]);

  useEffect(() => {
    initSpinTooltips(containerRef.current);
    return () => {
      containerRef.current?.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((el) => {
        window.bootstrap?.Tooltip.getInstance(el)?.dispose();
      });
    };
  }, [data, userSpins, isSpinning]);

  useEffect(
    () => () => {
      cancelAnimRef.current?.();
    },
    [],
  );

  const spinItems = data?.spinData ?? [];
  const dailySpins = userSpins?.daily_spins ?? 2;
  const availableSpins = userSpins?.available_spins ?? 0;
  const totalSpinsUsed = userSpins?.total_spins_used ?? 0;

  const resetDisabled =
    totalSpinsUsed === 0 || availableSpins === dailySpins;
  const playDisabled = availableSpins <= 0 || isSpinning;

  const spinCounterText = useMemo(
    () => t('spin_daily_spins', { max: dailySpins, used: totalSpinsUsed }),
    [t, dailySpins, totalSpinsUsed],
  );

  const reloadRecentRewards = useCallback(async () => {
    try {
      const rewards = await fetchRecentSpinRewards();
      setRecentList(rewards);
    } catch {
      /* ignore */
    }
  }, [fetchRecentSpinRewards]);

  const applyRemainingSpins = useCallback(
    (remainingSpins: number) => {
      const used = dailySpins - remainingSpins;
      setUserSpins((prev) =>
        prev
          ? {
              ...prev,
              available_spins: remainingSpins,
              total_spins_used: used,
            }
          : prev,
      );
    },
    [dailySpins],
  );

  const handleDeliverReward = useCallback(
    async (itemId: number) => {
      if (rewardDelivered) return;
      setRewardDelivered(true);
      try {
        const result = await deliverReward.mutateAsync(itemId);
        if (result.success) {
          await reloadRecentRewards();
        } else {
          setRewardDelivered(false);
        }
      } catch {
        setRewardDelivered(false);
      }
    },
    [deliverReward, rewardDelivered, reloadRecentRewards],
  );

  const openRewardModal = useCallback(
    (item: SpinWheelItem) => {
      setWonItem(item);
      setRewardModalOpen(true);
      void handleDeliverReward(item.id);
    },
    [handleDeliverReward],
  );

  const closeRewardModal = useCallback(() => {
    setRewardModalOpen(false);
    removeSpinWinningCheck(winningElRef.current);
    winningElRef.current = null;
    void refetch();
  }, [refetch]);

  const handleSpin = useCallback(async () => {
    if (playDisabled || isSpinning) return;

    setIsSpinning(true);
    setRewardDelivered(false);
    removeSpinWinningCheck(winningElRef.current);
    winningElRef.current = null;

    try {
      const result = await spin.mutateAsync();
      if (!result.success || !result.item) {
        showFlash(result.message ?? t('spin_error_spin'), 'danger');
        setIsSpinning(false);
        return;
      }

      if (result.remainingSpins !== undefined) {
        applyRemainingSpins(result.remainingSpins);
      }

      const gridEl = gridRef.current;
      const itemEls = gridEl
        ? (Array.from(gridEl.querySelectorAll('.spin-item')) as HTMLElement[])
        : [];

      let winningIndex = result.winningIndex ?? -1;
      if (winningIndex < 0) {
        winningIndex = itemEls.findIndex(
          (el) => Number(el.getAttribute('data-item-id')) === result.item!.id,
        );
      }

      if (winningIndex < 0 || winningIndex >= itemEls.length) {
        showFlash(t('spin_error_winner'), 'danger');
        setIsSpinning(false);
        return;
      }

      cancelAnimRef.current = runSpinCasinoAnimation({
        spinItems: itemEls,
        winningIndex,
        onComplete: (winningElement) => {
          winningElRef.current = winningElement;
          setIsSpinning(false);
          openRewardModal(result.item!);
        },
      });
    } catch {
      showFlash(t('spin_error_connection'), 'danger');
      setIsSpinning(false);
    }
  }, [
    applyRemainingSpins,
    isSpinning,
    openRewardModal,
    playDisabled,
    showFlash,
    spin,
    t,
  ]);

  const handleResetConfirm = useCallback(async () => {
    if (!resetDialog) return;
    setResetDialog({ phase: 'pending', message: t('silk_confirm_processing_msg') });
    try {
      const result = await resetSpins.mutateAsync();
      if (result.success) {
        setResetDialog({ phase: 'success', message: t('spin_reset_success') });
        showFlash(t('spin_reset_success'), 'success');
        await refetch();
      } else {
        setResetDialog({ phase: 'error', message: result.message ?? t('spin_reset_error') });
      }
    } catch {
      setResetDialog({ phase: 'error', message: t('spin_error_connection') });
    }
  }, [refetch, resetDialog, resetSpins, showFlash, t]);

  if (isError && !data) {
    return (
      <div className="container-ingame fade-in">
        <SimpleMenu title={t('spin_title')} />
        <div className="spin-container">
          <div className="spin-main-content">{t('spin_error_connection')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-ingame fade-in" ref={containerRef}>
      <IngameFlash flash={flash} />

      <div className="spin-container">
        <SimpleMenu title={t('spin_title')} />

        <div className="spin-main-content">
          <div className="spin-info">
            <div className="spin-counter">
              <span className="spin-text">{isLoading && !userSpins ? '…' : spinCounterText}</span>
              <button
                id="reset-spins-btn"
                type="button"
                className="spin-add-btn"
                title={`Reset spins (${RESET_SPIN_PRICE} Silk)`}
                disabled={resetDisabled || resetSpins.isPending}
                style={{ marginLeft: 8 }}
                onClick={() => setResetDialog({ phase: 'confirm' })}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 4V12M4 8H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </div>

          <div className="spin-flex-container">
            <div className="spin-items-grid-container">
              <div className="spin-items-grid" id="spinItemsGrid" ref={gridRef}>
                {spinItems.map((item, index) => (
                  <div
                    key={item.id}
                    className={`spin-item ${item.colorClass}`}
                    data-item-id={item.id}
                    data-item-index={index}
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    data-bs-html="true"
                    data-bs-title={`<div style="text-align:center"><strong>${item.name}</strong><br><span style="color:#a97c50;font-size:0.9em">${t('spin_probability')} ${item.probability.toFixed(2)}%</span></div>`}
                  >
                    <div className="spin-item-image-container">
                      <img
                        src={resolveMediaUrl(item.image)}
                        alt={item.name}
                        className="spin-item-image"
                        loading="eager"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = itemIconFallbackUrl();
                        }}
                      />
                      <div className="spin-item-quantity-badge">x{item.amount}</div>
                    </div>
                    <div className="spin-item-name">{item.name}</div>
                    <div className="spin-item-probability">{item.probability.toFixed(1)}%</div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                className={`spin-play-button ${playDisabled ? 'disabled' : 'active'}`}
                id="spinPlayButton"
                disabled={playDisabled}
                {...(playDisabled
                  ? {
                      'data-bs-toggle': 'tooltip',
                      'data-bs-placement': 'top',
                      'data-bs-title': t('spin_daily_reached_tooltip'),
                      'data-bs-custom-class': 'spin-disabled-tooltip',
                    }
                  : {})}
                onClick={() => void handleSpin()}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M8 5V19L19 12L8 5Z"
                    fill="currentColor"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            <div className="recent-rewards">
              <h2>{t('spin_recent_rewards')}</h2>
              <div className="rewards-list" id="rewardsList">
                {recentList.map((reward, i) => (
                  <RecentRewardItem
                    key={`${reward.name}-${reward.time_ago}-${i}`}
                    name={reward.name}
                    image={reward.image}
                    quantity={reward.quantity}
                    timeLabel={reward.time_ago}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <div
          id="rewardModal"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeRewardModal();
          }}
          onKeyDown={() => {}}
          style={{
            display: rewardModalOpen ? 'flex' : 'none',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div className="reward-modal-content">
            <div className="reward-modal-header">
              <h2>{t('spin_congratulations')}</h2>
              <button type="button" id="closeModal" className="spin-modal-close" onClick={closeRewardModal}>
                ×
              </button>
            </div>
            <div className="reward-modal-body">
              <div className="reward-image-wrapper">
                {wonItem && (
                  <img
                    src={resolveMediaUrl(wonItem.image)}
                    alt={wonItem.name}
                    id="rewardImage"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = itemIconFallbackUrl();
                    }}
                  />
                )}
              </div>
              <div className="reward-details">
                <span id="rewardName">{wonItem?.name ?? ''}</span>
                <span id="rewardAmount">x{wonItem?.amount ?? 0}</span>
              </div>
            </div>
          </div>
        </div>

        {resetDialog && (
          <SilkConfirmModal
            open
            phase={resetDialog.phase}
            title={t('spin_reset_confirm_title')}
            message={t('spin_reset_confirm_message')}
            cost={RESET_SPIN_PRICE}
            balance={resetDialog.phase === 'confirm' && silkLoading ? null : (silkBalance ?? null)}
            balanceLoading={resetDialog.phase === 'confirm' && silkLoading}
            balanceError={resetDialog.phase === 'confirm' && silkError}
            resultMessage={resetDialog.message}
            confirmLabel={t('spin_reset_confirm_confirm')}
            onConfirm={() => void handleResetConfirm()}
            onClose={() => setResetDialog(null)}
          />
        )}
      </div>
    </div>
  );
}
